import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { FileLogger } from "@comunicare/comunicare-helper";
import { FORMS_MODE } from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import { forkJoin, Subject, timer } from "rxjs";
import { first, takeUntil } from "rxjs/operators";
import { Base64Helper } from "src/app/helpers/Base64Helper";
import { AuthService } from "src/app/providers/auth.service";
import { HandleLinkService, IHandleLinkQuery, IRedirectionResult } from "src/app/providers/handle-link.service";
import { PreferenceService } from "src/app/providers/preference.service";
import { SessionService } from "src/app/providers/session.service";
import { ERROR_TYPE } from "../error-page/error-page.component";
import { LoginPageComponent } from "../login-page/login-page.component";

@Component({
  selector: "app-link-page",
  templateUrl: "./link-page.component.html",
  styleUrls: ["./link-page.component.scss"],
})
export class LinkPageComponent implements OnInit, OnDestroy {
  private onDestroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sessionService: SessionService,
    private handleLinkService: HandleLinkService,
    private dialog: MatDialog,
    private authService: AuthService,
    private preferenceService: PreferenceService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.onDestroy$)).subscribe((params) => {
      if (Object.keys(params).length === 0) {
        this.router.navigate(["/error"]);
        return;
      }

      const linkParams: IHandleLinkQuery = {
        token: params.token || params.shortid,
        pageid: params.pageid,
        modalid: params.modalid,
        userid: params.userid,
        deadline: params.deadline,
        patientid: params.patientid,
        serviceid: params.serviceid,
        crypt: params.crypt,
        phone: params.phone,
        birth: params.birth,
        sex: params.sex,
        lang: params.lang,
        line: params.line,
        postal: params.postal,
        city: params.city,
        country: params.country,
        internalId: params.internalId,
      };
      if (!params.token && !params.shortid) {
        FileLogger.error("LinkPageComponent", "Error no token provided");
        this.router.navigate(["/error"], { queryParams: { type: ERROR_TYPE.NOT_VALID_PARAMS } });
        return;
      }
      if (!this.sessionService.isLoggedIn() && !params.pageid && !params.modalid) {
        this.dialog
          .open(LoginPageComponent, {
            data: { isModal: true },
            disableClose: true,
          })
          .afterClosed()
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((success) => {
            if (success) {
              this.processLink(linkParams, false);
            }
          });
      } else {
        this.sessionService.logoutOnLeave = true; // with external link we disconnect the user when he leaves the dashboard
        this.processLink(linkParams, params.pageid || params.modalid);
      }
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private processLink(linkParams: IHandleLinkQuery, isExternalLink: boolean): void {
    if (isExternalLink && this.sessionService.isLoggedIn()) {
      this.sessionService.skip2FA = false;
      this.authService.logout();
      this.sessionService.logoutOnLeave = true; // with external link we disconnect the user when he leaves the dashboard
    }
    forkJoin({
      response: this.handleLinkService.handleLink(linkParams).pipe(takeUntil(this.onDestroy$)),
      delay: timer(5000), // Display loader at least 5 seconds before redirecting to avoid weird jumps
    }).subscribe(
      ({ response }) => {
        if (response.headers["x-access-token"]) {
          // automatic login : setup token and language
          this.sessionService.token = response.headers["x-access-token"];
          this.preferenceService
            .getGlobal()
            .pipe(first())
            .subscribe(
              (globalParam) => {
                if (globalParam?.preferredLanguage) {
                  this.translateService.use(globalParam.preferredLanguage);
                  this.sessionService.userLang = globalParam.preferredLanguage;
                }
                this.handleRedirection(response, isExternalLink, linkParams.crypt);
              },
              (err) => {
                FileLogger.error("LinkPageComponent", "Error getting user preferred Language ", err);
                this.handleRedirection(response, isExternalLink, linkParams.crypt);
              }
            );
        } else {
          this.handleRedirection(response, isExternalLink, linkParams.crypt);
        }
      },
      (error) => {
        FileLogger.error("LinkPageComponent", "Error calling handle-link", error);
        this.router.navigate(["/error"]);
        return;
      }
    );
  }

  private handleRedirection(response: IRedirectionResult, isExternalLink: boolean, crypt?: string): void {
    const headers = response.headers;

    if (headers["error-type"]) {
      this.router.navigate(["/error"], { queryParams: { type: headers["error-type"] } });
      return;
    }

    const redirectPageId = headers["redirect-pageid"];
    if (!redirectPageId) {
      this.router.navigate(["/dashboard"]);
      return;
    }

    switch (redirectPageId) {
      case "patient":
        if (headers["redirect-patientid"]) {
          const b64Id = Base64Helper.utf8_to_b64(headers["redirect-patientid"]);
          this.router.navigate(["patient", { id: b64Id }], {
            state: {
              caremateIdentifier: headers["redirect-patientid"],
            },
          });
          return;
        }
        break;
      case "patientRegistration/patient":
        this.router.navigate(["patientRegistration", "patient"], {
          state: {
            mode: FORMS_MODE.CREATE,
            source: headers["source"],
            linkData: response?.data?.length ? response.data : undefined,
          },
        });
        return;

      default: {
        const queryParams: any = {};
        if (headers["redirect-serviceid"]) {
          queryParams.serviceid = headers["redirect-serviceid"];
        }
        if (headers["redirect-organization"]) {
          queryParams.organization = headers["redirect-organization"];
        }

        if ((crypt === undefined || crypt === "true") && response.headers["x-access-token"] && isExternalLink) {
          this.sessionService.skip2FA = true;
        }
        this.router.navigate([`${redirectPageId}`], Object.keys(queryParams).length > 0 ? { queryParams } : undefined);
        return;
      }
    }
  }
}
